import { FC, Fragment, createPortal } from "preact/compat";
import { useCallback } from "preact/hooks";
import { Transition } from "@headlessui/react";
import { useDialogUrlState, usePortalTarget } from "~/hooks";
import { IconButton } from "../IconButton";
import { FullscreenDialogProps } from "./interfaces";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import cn from "classnames";

const NO_FULLSCREEN = Boolean(localStorage.getItem("no-fullscreen"));

const FullscreenDialog: FC<FullscreenDialogProps> = ({
    show,
    title,
    children,
    backdropClassName,
    hideTitle,
    hideClose,
    noUrlState,
    onClose,
    beforeEnter,
    afterEnter,
    beforeLeave,
    afterLeave,
}) => {
    const target = usePortalTarget("fs-dialog");
    const defHandleClose = useCallback(() => (show.value = false), [show]);

    useDialogUrlState(show, { skip: noUrlState, onClose });

    if (!target.value) {
        return null;
    }

    const handleClose = onClose ?? defHandleClose;

    return createPortal(
        <Transition
            show={show.value}
            as={Fragment}
            beforeEnter={() => {
                if (
                    "requestFullscreen" in document.documentElement &&
                    !NO_FULLSCREEN
                ) {
                    target.value?.requestFullscreen().catch(() => {});
                }

                beforeEnter?.();
            }}
            beforeLeave={() => {
                if (
                    "exitFullscreen" in document &&
                    document.fullscreenElement
                ) {
                    document.exitFullscreen();
                }

                beforeLeave?.();
            }}
            afterEnter={afterEnter}
            afterLeave={afterLeave}
            enter="transform-opacity transition duration-100 ease-in"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transform-opacity transition duration-100 ease-out"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div
                role="dialog"
                aria-modal="true"
                className={cn(
                    "fixed inset-0 z-[10000] flex flex-col bg-black/90",
                    backdropClassName,
                )}
            >
                <div
                    className={cn(
                        "flex justify-between p-4 transition duration-200 z-10 bg-black/60 backdrop-blur pt-4 mobile:pt-[var(--safe-top)]",
                        hideTitle && "-translate-y-full",
                    )}
                >
                    <h2 className="text-xl font-bold text-white">{title}</h2>
                </div>
                <div
                    className={cn(
                        "absolute top-[calc(var(--safe-top))] right-4 z-10 transition duration-200 flex",
                        hideTitle &&
                            "p-1 -mt-1 -mr-1 bg-black/60 backdrop-blur rounded-full",
                        hideClose && "opacity-0 -translate-y-11",
                    )}
                >
                    <IconButton title="Close" flat onClick={handleClose}>
                        <CloseIcon
                            width="24"
                            height="24"
                            className="fill-white"
                        />
                    </IconButton>
                </div>
                {children}
            </div>
        </Transition>,
        target.value,
    );
};

export default FullscreenDialog;
